<script lang="ts">
  import { env as envPublic } from '$env/dynamic/public';
  import { onMount } from 'svelte';
  import { afterNavigate } from '$app/navigation';
  import { page } from '$app/stores';
  import { browser } from '$app/environment';
  import { Dropdown, DropdownItem, Select } from 'flowbite-svelte';
  import { selectedCategoryName } from '$lib/stores';
  import SearchInput from '$components/locations/+searchInput.svelte';
  import { updateUrlParams } from '$utils/updateUrlParams';
  import type { CategoriesData } from '$types';

  /**
   * Title of the filter section
   */
  export let title: string = '';

  /**
   * First item (basically reset categories when clicked)
   */
  export let allCategoriesText: string = 'Partnere';

  /**
   * Title of the mobile alternative filter section
   */
  export let alternativeTitle: string = '';

  /**
   * Use alternative mobile look
   */
  export let alternativeLook: boolean = false;

  /**
   * Full list of categories.
   * Expected to be an object like { data: [{ id, name }, ...] }
   */
  export let categories: CategoriesData = { data: [] };

  /**
   * Categories to show directly (pinned).
   * Expected to be an object like { data: [{ id, name }, ...] }
   */
  export let selectedCategories: CategoriesData = { data: [] };

  /**
   * Enable the search input component
   */
  export let searchInputEnabled: boolean = true;

  /**
   * URL to redirect to after filter is applied
   */
  export let redirectUrl: string = '';

  /**
   * The query parameter key for category
   */
  export let categoryParam: string = 'filter[category]';

  /**
   * The query parameter key for search (mostly  we need to pass it to <SearchInput>)
   * @default 'filter[s]'
   */
  export let searchParam: string = 'filter[s]';

  /** The currently selected category id */
  let selected = 0;

  /** Whether the "More categories" dropdown is open */
  let dropdownOpen = false;

  /**
   * Reads the current category from the URL param (categoryParam)
   * and updates the local `selected` + `selectedCategoryName`.
   */
  function syncSelectedWithUrl() {
    // Only run if on client and we have page.url
    if (!browser) return;

    const searchParams = new URLSearchParams($page.url.search);
    const paramValue = searchParams.get(categoryParam);

    if (paramValue) {
      const categoryId = Number(paramValue);
      selected = categoryId;

      // Find the matching category in the full list
      const catObj = categories.data.find((c) => c.id === categoryId);
      if (catObj) {
        selectedCategoryName.set(catObj.name);
      } else {
        // Fallback if not found
        selectedCategoryName.set(allCategoriesText);
      }
    } else {
      // No param => "all categories"
      selected = 0;
      selectedCategoryName.set(allCategoriesText);
    }
  }

  /**
   * When a user clicks a category (desktop or in a dropdown),
   * update the URL param and local states.
   *
   * @param {number|string} categoryId The category ID to filter
   * @param {string} categoryName The category name to display
   */
  function handleFilterClick(categoryId: number | string, categoryName: string) {
    if (browser) {
      updateUrlParams(
        { [categoryParam]: categoryId },
        redirectUrl,
        true, // pushState = true
        false, // do not scroll
      );
    }
    selected = Number(categoryId);
    selectedCategoryName.set(categoryName);
    dropdownOpen = false;
  }

  /**
   * Handles change in the mobile <Select> for categories.
   */
  function handleFilterChange() {
    const selectElement = document.getElementById('select-filters') as HTMLSelectElement | null;
    const selectedOption = selectElement?.options[selectElement.selectedIndex];
    if (selectedOption) {
      const categoryId = selectedOption.value;
      const categoryName = selectedOption.text;
      if (categoryId === '0') {
        // Reset: remove filter parameter and show default text
        handleFilterClick('', allCategoriesText);
      } else {
        handleFilterClick(categoryId, categoryName);
      }
    }
  }

  // On mount (first client load), sync once
  onMount(() => {
    syncSelectedWithUrl();
  });

  // After every navigation (including back/forward), re-sync
  afterNavigate(() => {
    syncSelectedWithUrl();
  });

  // Derived array: all categories not already in selectedCategories
  $: remainingCategories = categories.data.filter((cat) => !selectedCategories.data.some((sc) => sc.id === cat.id));
</script>

<section class={alternativeLook ? 'section-filters section-filters-fullwidth' : 'section-filters'}>
  {#if title}
    <h2>{title}</h2>
  {/if}

  <!-- DESKTOP FILTERS -->
  <div class="filters">
    <ul>
      <li>
        <!-- "All categories" button -->
        <button on:click={() => handleFilterClick('', allCategoriesText)}>
          {allCategoriesText}
        </button>
      </li>

      <!-- Pinned categories directly shown -->
      {#each selectedCategories.data as selectCategory}
        <li class={selectCategory.id === selected ? 'active' : ''}>
          <button on:click={() => handleFilterClick(selectCategory.id, selectCategory.name)}>
            {selectCategory.name}
          </button>
        </li>
      {/each}

      <!-- Remaining categories in a "More" dropdown -->
      {#if remainingCategories.length > 0}
        <li class="moreCat-wrapper">
          <button class="moreCat">
            Flere kategorier
            <!-- example SVG icon -->
            <svg width="17px" height="17px" fill="red" viewBox="0 0 24 24">
              <g>
                <path d="M12 20c-.39 0-.78-.15-1.06-.44l-7-7a1.5 1.5 0 0 1 2.12-2.12L12 16.88l6.94-6.94a1.5 1.5 0 0 1 2.12 2.12l-7 7c-.28.28-.67.44-1.06.44z" />
              </g>
            </svg>
          </button>
          <Dropdown triggeredBy=".moreCat" class="dropdown" classDefault="moreCat-container" bind:open={dropdownOpen}>
            {#each remainingCategories as category}
              <DropdownItem on:click={() => handleFilterClick(category.id, category.name)}>
                {category.name}
              </DropdownItem>
            {/each}
          </Dropdown>
        </li>
      {/if}
    </ul>

    <!-- Optional search input -->
    {#if searchInputEnabled}
      <SearchInput {searchParam} {redirectUrl} searchLayout="button" liveSearchEnabled={false} />
    {/if}
  </div>

  <!-- MOBILE FILTERS -->
  <div class={alternativeLook ? 'mobile-filters mobile-filters-hide' : 'mobile-filters'}>
    <ul class="main-filter-list">
      <li class="main-filter-list-item">
        <Select id="select-filters" bind:value={selected} on:change={handleFilterChange} class="dropdown">
          <option value={0}>Alle kategorier</option>
          {#each categories.data as category}
            <option value={category.id}>{category.name}</option>
          {/each}
        </Select>
      </li>
    </ul>
    {#if searchInputEnabled}
      <SearchInput {redirectUrl} searchLayout="button" liveSearchEnabled={false} />
    {/if}
  </div>

  <!-- Optional alternative mobile look for pinned categories -->
  {#if alternativeLook}
    {#if alternativeTitle}
      <h2 class="mobile-categories-title">
        {alternativeTitle}
      </h2>
    {/if}
    <div class="mobile-categories">
      {#each selectedCategories.data as category (category.id)}
        <a href={`/eshop/find-shop?category=${category.id}`} class="category-link">
          <div class="mobile-category-container">
            <div class="mobile-category-icon-container">
              <img src={`/${envPublic.PUBLIC_PUBLISHER}/eshop/categories/category-${category.id}.svg`} alt="{category.name} Icon" />
            </div>
            <div class="mobile-category-title">{category.name}</div>
          </div>
        </a>
      {/each}
    </div>
  {/if}
</section>
